import React, { useEffect, useState } from 'react';
import { getUsers, createUser, updateUser, deleteUser, bulkUsers } from '../../lib/api';
import { BulkActionBar, SelectCheckbox } from '../BulkActionBar';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Eye, EyeOff, Pencil, Trash2, UserPlus } from 'lucide-react';
import { toast } from 'sonner';

export const UsersTab = ({ currentUser }) => {
    const [users, setUsers] = useState([]);
    const [editingUser, setEditingUser] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [deleteUserId, setDeleteUserId] = useState(null);
    const [selectedUserIds, setSelectedUserIds] = useState([]);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [newUserForm, setNewUserForm] = useState({ name: '', email: '', password: '', role: 'commercial' });
    const [showPassword, setShowPassword] = useState(false);
    const [creating, setCreating] = useState(false);

    const load = async () => {
        try {
            const res = await getUsers();
            setUsers(res.data);
        } catch {
            setUsers([]);
        }
    };
    useEffect(() => { load(); }, []);

    const handleUpdate = async () => {
        if (!editingUser) return;
        const payload = { name: editingUser.name, email: editingUser.email, role: editingUser.role };
        if (editingUser.newPassword && editingUser.newPassword.trim() !== '') {
            if (editingUser.newPassword.length < 6) {
                toast.error('Le mot de passe doit contenir au moins 6 caractères');
                return;
            }
            payload.password = editingUser.newPassword;
        }
        try {
            await updateUser(editingUser.id, payload);
            toast.success(payload.password ? 'Utilisateur mis à jour (mot de passe réinitialisé)' : 'Utilisateur mis à jour');
            setShowEditModal(false);
            setEditingUser(null);
            load();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
        }
    };

    const handleCreate = async () => {
        if (!newUserForm.name || !newUserForm.email || !newUserForm.password) {
            toast.error('Veuillez remplir tous les champs obligatoires');
            return;
        }
        if (newUserForm.password.length < 6) {
            toast.error('Le mot de passe doit contenir au moins 6 caractères');
            return;
        }
        setCreating(true);
        try {
            await createUser(newUserForm);
            toast.success('Utilisateur créé avec succès');
            setShowCreateModal(false);
            setNewUserForm({ name: '', email: '', password: '', role: 'commercial' });
            load();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la création');
        } finally {
            setCreating(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteUserId) return;
        try {
            await deleteUser(deleteUserId);
            toast.success('Utilisateur supprimé');
            setDeleteUserId(null);
            load();
        } catch {
            toast.error('Erreur lors de la suppression');
        }
    };

    const selectableUsers = users.filter((u) => u.id !== currentUser?.id);

    return (
        <div className="bg-white border border-[#E4E4E7]">
            <div className="p-4 border-b border-[#E4E4E7] flex items-center justify-between">
                <h3 className="font-semibold text-[#0A0A0A]">Gestion des utilisateurs</h3>
                <Button onClick={() => setShowCreateModal(true)} className="bg-[#E85A24] hover:bg-[#CC4D1C] text-white rounded-sm" data-testid="new-user-btn">
                    <UserPlus className="w-4 h-4 mr-2" /> Nouvel utilisateur
                </Button>
            </div>

            {selectedUserIds.length > 0 && (
                <div className="p-3 border-b border-[#E4E4E7]">
                    <BulkActionBar
                        selectedIds={selectedUserIds}
                        entityLabel="utilisateur(s)"
                        onClear={() => setSelectedUserIds([])}
                        actions={[]}
                        onDelete={async () => {
                            await bulkUsers('delete', selectedUserIds);
                            setSelectedUserIds([]);
                            load();
                        }}
                    />
                </div>
            )}

            <div className="px-4 py-2 border-b border-[#E4E4E7] flex items-center gap-3 text-xs text-[#52525B] bg-[#FAFAFA]">
                <SelectCheckbox
                    checked={selectedUserIds.length > 0 && selectedUserIds.length === selectableUsers.length}
                    onChange={(checked) => {
                        if (checked) setSelectedUserIds(selectableUsers.map((u) => u.id));
                        else setSelectedUserIds([]);
                    }}
                    dataTestid="select-all-users"
                />
                <span>Tout sélectionner (hors votre compte)</span>
            </div>

            <div className="divide-y divide-[#E4E4E7]">
                {users.map((u) => (
                    <div key={u.id} className="flex items-center justify-between p-4" data-testid={`user-${u.id}`}>
                        <div className="flex items-center gap-3">
                            {u.id !== currentUser?.id ? (
                                <SelectCheckbox
                                    checked={selectedUserIds.includes(u.id)}
                                    onChange={(checked) => {
                                        setSelectedUserIds((prev) => (checked ? [...prev, u.id] : prev.filter((x) => x !== u.id)));
                                    }}
                                    dataTestid={`select-user-${u.id}`}
                                />
                            ) : (
                                <div className="w-4" />
                            )}
                            <div className="w-10 h-10 rounded-full bg-[#E85A24] flex items-center justify-center text-white font-bold">
                                {u.name?.charAt(0).toUpperCase()}
                            </div>
                            <div>
                                <p className="font-medium">{u.name}</p>
                                <p className="text-sm text-[#52525B]">{u.email}</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <span className={`px-2 py-1 text-xs font-semibold rounded-sm ${
                                u.role === 'admin' ? 'bg-[#E85A24]/10 text-[#E85A24]' :
                                u.role === 'manager' ? 'bg-[#7C3AED]/10 text-[#7C3AED]' :
                                'bg-[#52525B]/10 text-[#52525B]'
                            }`}>
                                {u.role}
                            </span>
                            {u.id !== currentUser?.id && (
                                <div className="flex gap-1">
                                    <Button variant="ghost" size="icon" onClick={() => { setEditingUser(u); setShowEditModal(true); }}>
                                        <Pencil className="w-4 h-4" />
                                    </Button>
                                    <Button variant="ghost" size="icon" onClick={() => setDeleteUserId(u.id)} className="text-[#DC2626]">
                                        <Trash2 className="w-4 h-4" />
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {/* Edit Dialog */}
            <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
                <DialogContent data-testid="edit-user-dialog">
                    <DialogHeader>
                        <DialogTitle>Modifier l'utilisateur</DialogTitle>
                    </DialogHeader>
                    {editingUser && (
                        <div className="space-y-4">
                            <div>
                                <Label className="field-label">Nom</Label>
                                <Input value={editingUser.name} onChange={(e) => setEditingUser((prev) => ({ ...prev, name: e.target.value }))} className="h-10 rounded-sm mt-1" data-testid="edit-user-name" />
                            </div>
                            <div>
                                <Label className="field-label">Email</Label>
                                <Input type="email" value={editingUser.email || ''} onChange={(e) => setEditingUser((prev) => ({ ...prev, email: e.target.value }))} className="h-10 rounded-sm mt-1" data-testid="edit-user-email" />
                            </div>
                            <div>
                                <Label className="field-label">Rôle</Label>
                                <Select value={editingUser.role} onValueChange={(v) => setEditingUser((prev) => ({ ...prev, role: v }))}>
                                    <SelectTrigger className="h-10 rounded-sm mt-1"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="commercial">Commercial</SelectItem>
                                        <SelectItem value="conducteur">Conducteur de travaux</SelectItem>
                                        <SelectItem value="manager">Manager</SelectItem>
                                        <SelectItem value="admin">Administrateur</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="pt-4 border-t border-[#E4E4E7]">
                                <Label className="field-label">Réinitialiser le mot de passe</Label>
                                <div className="relative mt-1">
                                    <Input type={showPassword ? 'text' : 'password'} value={editingUser.newPassword || ''} onChange={(e) => setEditingUser((prev) => ({ ...prev, newPassword: e.target.value }))} placeholder="Laisser vide pour ne pas changer" className="h-10 rounded-sm pr-10" data-testid="edit-user-password" />
                                    <button type="button" onClick={() => setShowPassword((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#52525B]">
                                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </button>
                                </div>
                                <p className="text-xs text-[#52525B] mt-1">Minimum 6 caractères. L'utilisateur devra se reconnecter.</p>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowEditModal(false)}>Annuler</Button>
                        <Button onClick={handleUpdate} className="bg-[#E85A24] hover:bg-[#CC4D1C] text-white" data-testid="save-user-btn">Enregistrer</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Create Dialog */}
            <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <UserPlus className="w-5 h-5 text-[#E85A24]" />
                            Créer un nouvel utilisateur
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label className="field-label">Nom complet *</Label>
                            <Input value={newUserForm.name} onChange={(e) => setNewUserForm((prev) => ({ ...prev, name: e.target.value }))} placeholder="Jean Dupont" className="h-11 rounded-sm mt-1" data-testid="new-user-name" />
                        </div>
                        <div>
                            <Label className="field-label">Email *</Label>
                            <Input type="email" value={newUserForm.email} onChange={(e) => setNewUserForm((prev) => ({ ...prev, email: e.target.value }))} placeholder="jean.dupont@uneedservices.fr" className="h-11 rounded-sm mt-1" data-testid="new-user-email" />
                        </div>
                        <div>
                            <Label className="field-label">Mot de passe *</Label>
                            <div className="relative">
                                <Input type={showPassword ? 'text' : 'password'} value={newUserForm.password} onChange={(e) => setNewUserForm((prev) => ({ ...prev, password: e.target.value }))} placeholder="Minimum 6 caractères" className="h-11 rounded-sm mt-1 pr-12" data-testid="new-user-password" />
                                <button type="button" onClick={() => setShowPassword((s) => !s)} className="absolute right-4 top-1/2 -translate-y-1/2 text-[#52525B] hover:text-[#0A0A0A] mt-0.5">
                                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                </button>
                            </div>
                        </div>
                        <div>
                            <Label className="field-label">Rôle *</Label>
                            <Select value={newUserForm.role} onValueChange={(v) => setNewUserForm((prev) => ({ ...prev, role: v }))}>
                                <SelectTrigger className="h-11 rounded-sm mt-1" data-testid="new-user-role"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="commercial">Commercial</SelectItem>
                                    <SelectItem value="conducteur">Conducteur de travaux</SelectItem>
                                    <SelectItem value="manager">Manager</SelectItem>
                                </SelectContent>
                            </Select>
                            <p className="text-xs text-[#52525B] mt-2">
                                <strong>Commercial :</strong> Gestion de son portefeuille prospects/clients<br/>
                                <strong>Conducteur :</strong> Suivi opérationnel des chantiers<br/>
                                <strong>Manager :</strong> Accès aux rapports et supervision
                            </p>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowCreateModal(false)}>Annuler</Button>
                        <Button onClick={handleCreate} disabled={creating} className="bg-[#E85A24] hover:bg-[#CC4D1C] text-white" data-testid="create-user-submit">
                            {creating ? 'Création...' : "Créer l'utilisateur"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation */}
            <AlertDialog open={!!deleteUserId} onOpenChange={() => setDeleteUserId(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
                        <AlertDialogDescription>Êtes-vous sûr de vouloir supprimer cet utilisateur ? Cette action est irréversible.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Annuler</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete} className="bg-[#DC2626] hover:bg-[#DC2626]/90">Supprimer</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};
